import { Controller } from "@hotwired/stimulus";
import * as d3 from "d3";
import moment from "moment";

export default class extends Controller {
  static values = {
    showPrices: { type: Boolean, default: false },
    showDepartAt: { type: Boolean, default: true },
    departAt: String,
    safeDepartAt: String,
    planning: Object,
    solar: { type: Array, default: [] },
    prices: Array,
    currency: String,
  };

  connect() {
    this.element.innerHTML = "";

    const showPrices = this.showPricesValue;
    const currency = this.currencyValue;

    const margin = { top: 2, right: 45, bottom: 35, left: 0 };
    const width = this.element.offsetWidth - margin.left - margin.right;
    const height = 144 - margin.top - margin.bottom;

    const svg = d3
      .select(this.element)
      .append("svg")
      .attr("width", width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom)
      .append("g")
      .attr("transform", "translate(" + margin.left + "," + margin.top + ")");

    let usedDepartAt = this.departAtValue;

    let departAt;

    if (usedDepartAt !== "") {
      departAt = moment(usedDepartAt);
    } else {
      departAt = moment().add(2, "days");
    }

    const atLeast = moment.max(moment(departAt).endOf("day"), moment().add(24, "hours").endOf("day"));

    const xDomain = [];
    xDomain[0] = moment();
    xDomain[1] = moment.min(atLeast, moment().add(2, "days").endOf("day"));

    const data = this.pricesValue
      .map((d) => {
        return {
          periodStart: moment(d["period_start"]),
          price: parseFloat(d["price"]),
          priceForecast: parseFloat(d["price_forecast"]),
          priceForecastLower: parseFloat(d["price_forecast_lower"]),
          priceForecastUpper: parseFloat(d["price_forecast_upper"]),
        };
      })
      .filter((d) => d.periodStart <= xDomain[1]);

    if (data.length < 2) return;

    const pricePeriodDuration = data[data.length - 1].periodStart.diff(data[data.length - 2].periodStart);
    const closingPrice = { ...data[data.length - 1] };
    closingPrice.periodStart = moment(closingPrice.periodStart).add(pricePeriodDuration);
    data[data.length] = closingPrice;

    const x = d3
      .scaleUtc()
      .domain(xDomain)
      .range([0, showPrices ? width : width + margin.right]);

    const allPrices = data
      .map((d) => {
        return [d.price, d.priceForecast, d.priceForecastLower, d.priceForecastUpper];
      })
      .flat();

    const yDomain = d3.extent(allPrices);
    const yDelta = yDomain[1] - yDomain[0];
    yDomain[0] -= yDelta * 0.1;
    yDomain[1] += yDelta * 0.1;
    let y = d3.scaleLinear().domain(yDomain).range([height, 0]);

    if (this.hasPlanningValue) {
      const planning = Object.keys(this.planningValue).map((date) => {
        return {
          periodStart: moment(date),
          kW: this.planningValue[date],
        };
      });

      let yPlanning = d3
        .scaleLinear()
        .domain(d3.extent(planning, (d) => d.kW))
        .range([height, -20]);

      svg
        .append("g")
        .datum(planning)
        .attr("class", "fill-current stroke-current text-energy-fill-2")
        .attr("stroke-width", 1)
        .call((group) => {
          group.append("path").attr(
            "d",
            d3
              .area()
              .curve(d3.curveStepAfter)
              .x((d) => x(d.periodStart))
              .y0(yPlanning(0))
              .y1((d) => yPlanning(d.kW)),
          );
        });

      svg
        .datum(planning)
        .append("mask")
        .attr("id", "planning-mask")
        .attr("class", "fill-current text-detail-4")
        .append("path")
        .attr(
          "d",
          d3
            .area()
            .curve(d3.curveStepAfter)
            .x((d) => x(d.periodStart))
            .y0(yPlanning(0))
            .y1((d) => yPlanning(d.kW)),
        );
    }

    const visibleForecasts = d3.filter(data, (d) => d.price === null);

    const upperForecastSpread = visibleForecasts.map((d) => ({
      periodStart: d.periodStart,
      y: d.priceForecastUpper,
    }));

    const lowerForecastSpread = visibleForecasts.map((d) => {
      return {
        periodStart: d.periodStart,
        y: d.priceForecastLower,
      };
    });

    const forecastShape = upperForecastSpread.concat(lowerForecastSpread.reverse());

    svg
      .append("g")
      .datum(forecastShape)
      .attr("class", "fill-current stroke-current text-detail-2")
      .attr("opacity", 0.05)
      .call((group) => {
        group.append("path").attr(
          "d",
          d3
            .line()
            .curve(d3.curveStepAfter)
            .x((d) => x(d.periodStart))
            .y((d) => y(d.y)),
        );
      });

    const priceLine = svg
      .datum(data)
      .append("g")
      .attr("class", "price-line")
      .call((g) =>
        g
          .append("path")
          .attr("class", "stroke-current text-detail-3")
          .attr("fill", "none")
          .attr("stroke-width", 1.5)
          .attr("stroke-linejoin", "miter")
          .attr(
            "d",
            d3
              .line()
              .curve(d3.curveStepAfter)
              .x((d) => x(d.periodStart))
              .y((d) => y(d.price)),
          ),
      )
      .call((g) =>
        g
          .append("path")
          .attr("class", "stroke-current text-detail-3")
          .attr("fill", "none")
          .attr("stroke-width", 1.5)
          .attr("stroke-dasharray", "1.5 3")
          .attr("stroke-linejoin", "miter")
          .attr(
            "d",
            d3
              .line()
              .defined((d, i, data) => {
                const nextItem = data[i + 1];
                return !nextItem?.price;
              })
              .curve(d3.curveStepAfter)
              .x((d) => x(d.periodStart))
              .y((d) => y(d.price || d.priceForecast)),
          ),
      );

    priceLine
      .clone(true)
      .attr("mask", "url(#planning-mask)")
      .selectAll("path")
      .attr("class", "stroke-current text-action-fill");

    if (showPrices) {
      const yAxis = svg
        .append("g")
        .classed("axis", true)
        .attr("transform", "translate(" + width + ", 0)")
        .call(
          d3
            .axisRight(y)
            .ticks(8)
            .tickFormat((d) => d3.formatLocale({ currency: [currency, ""] }).format("$.2f")(d)),
        )
        .call((g) => {
          return g
            .selectAll(".tick line")
            .clone()
            .attr("stroke-opacity", (d) => (d === 0 ? 1 : 0.1))
            .attr("stroke", "black")
            .attr("x", 0)
            .attr("x2", -width);
        });
    }

    const safeDepartAt = moment(this.safeDepartAtValue);

    if (xDomain[0] <= departAt && departAt <= xDomain[1] && this.showDepartAtValue) {
      svg
        .append("line")
        .attr("x1", x(departAt))
        .attr("y1", -2)
        .attr("x2", x(departAt))
        .attr("y2", height)
        .style("stroke-width", 2)
        .attr("class", "stroke-current text-energy-fill-fast")
        .style("fill", "none");
    }

    if (xDomain[0] <= safeDepartAt && safeDepartAt <= xDomain[1] && !departAt.isSame(safeDepartAt)) {
      svg
        .append("line")
        .attr("x1", x(safeDepartAt))
        .attr("y1", -2)
        .attr("x2", x(safeDepartAt))
        .attr("y2", height)
        .style("stroke-width", 2)
        .attr("class", "stroke-current text-yellow")
        .style("fill", "none");
    }

    const everySixHours = d3.timeHour.every(6).range(...xDomain);
    const xAxis = svg
      .append("g")
      .classed("axis", true)
      .attr("transform", "translate(0," + height + ")")
      .call(
        d3
          .axisBottom(x)
          .tickValues(everySixHours)
          .tickFormat((d) => moment(d).format("H\\h")),
      )
      .call((g) => g.selectAll(".tick line").clone().attr("y", 0).attr("y2", -height))
      .call((g) => g.selectAll("text").attr("x", 4).style("text-anchor", "start").classed("text-detail-3", true))
      .call((g) => g.selectAll(".tick line").classed("stroke-current text-detail-4", true))
      .call((g) => g.selectAll(".tick line:first-of-type").attr("y2", 16));

    const midnights = d3.timeHour.every(24).range(...xDomain);
    svg
      .append("g")
      .classed("axis midnights", true)
      .attr("transform", "translate(0," + height + ")")
      .call(
        d3
          .axisBottom(x)
          .tickValues(midnights)
          .tickFormat((d) => moment(d).format("LL")),
      )
      .call((g) => g.selectAll("text").attr("x", 4).attr("y", 22).style("text-anchor", "start"))
      .call((g) =>
        g
          .selectAll(".tick line:first-of-type")
          .classed("stroke-current text-detail-3", true)
          .attr("y", 16)
          .attr("y2", 40),
      );

    svg
      .selectAll(".axis")
      .call((axis) =>
        axis.selectAll("text").classed("text-detail-2", (d) => {
          return d !== 0;
        }),
      )
      .call((axis) => axis.selectAll("line").classed("text-detail-2", true))
      .call((axis) => axis.selectAll("path").classed("text-detail-2", true));

    if (this.solarValue.length > 0) {
      let solar = this.solarValue.map((date) => {
        return {
          timestamp: moment(date.timestamp),
          value: date.watts,
        };
      });

      let ySolar = d3
        .scaleLinear()
        .domain(d3.extent(solar, (d) => d.value))
        .range([height, 30]);

      const solarLine = svg
        .datum(solar)
        .append("g")
        .attr("class", "solar-line")
        .call((g) =>
          g
            .append("path")
            .attr("class", "fill-current stroke-current text-energy-fill-solar opacity-20")
            .attr("stroke-width", 1)
            .attr("stroke-linejoin", "miter")
            .attr(
              "d",
              d3
                .area()
                .x((d) => x(d.timestamp))
                .y1((d) => ySolar(d.value))
                .y0((d) => ySolar(0)),
            ),
        );
    }
  }

  disconnect() {
    this.element.innerHTML = "";
  }
}
